import styled from "@emotion/styled";
import React, { Dispatch, SetStateAction, useState } from "react";
import { PAYMENT_TYPE } from "@ecologi/shared";
import { Button } from "../Button";
import { Heading } from "../Heading";
import { DialogModal } from "../DialogModal/DialogModal";

const Bold = styled.span`
  font-weight: bold;
`;

const ButtonControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;

  @media ${({ theme }) => theme.bp.mobileUp} {
    flex-direction: row;
    justify-content: space-between;
    gap: initial;
  }
`;

const CheckoutModal = styled(DialogModal)`
  width: 90%;
  max-width: 700px;
  padding: 48px;

  @media ${({ theme }) => theme.bp.mobileUp} {
    padding: 64px;
  }
`;

const Paragraph = styled.p`
  margin-bottom: 32px;
`;

const NewLine = styled.p`
  margin-top: 20px;
`;

const StyledHeading = styled(Heading)`
  margin-bottom: 18px;
`;

export type ImpactShopCheckoutModalProps = {
  formattedTotalCost: string;
  handleConfirmPayment: () => Promise<void>;
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  maskedCardNumber: string;
  basketItemCount: number;
  paymentType: PAYMENT_TYPE;
};

export const ImpactShopCheckoutModal = ({
  formattedTotalCost,
  handleConfirmPayment,
  isModalOpen,
  setIsModalOpen,
  maskedCardNumber,
  basketItemCount,
  paymentType,
}: ImpactShopCheckoutModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const handleClose = () => {
    setIsModalOpen(false);
  };

  return (
    <CheckoutModal onClose={handleClose} isOpen={isModalOpen} inline>
      <StyledHeading as={"h2"} size={5}>
        {paymentType === PAYMENT_TYPE.CARD ? (
          <>You'll pay with your existing card</>
        ) : (
          <>You will receive an invoice via email</>
        )}
      </StyledHeading>
      <Paragraph>
        {paymentType === PAYMENT_TYPE.CARD ? (
          <>
            Your total ({basketItemCount} item{basketItemCount > 1 ? "s" : ""})
            is <Bold>{formattedTotalCost}</Bold>. Your saved card (ending in{" "}
            <Bold>{maskedCardNumber}</Bold>) will be charged directly.
            <NewLine>Do you wish to continue?</NewLine>
          </>
        ) : (
          <>
            Your total ({basketItemCount} item{basketItemCount > 1 ? "s" : ""})
            is <Bold>{formattedTotalCost}</Bold>. Your invoice will be sent to
            your billing email address via our payment provider Stripe. You will
            have 30 days to make the payment. Once payment is received your
            impact will then be added to your profile.{" "}
            <NewLine>Do you wish to continue?</NewLine>
          </>
        )}
      </Paragraph>
      <ButtonControls>
        <Button
          onClick={async () => {
            setIsLoading(true);
            await handleConfirmPayment();
            setIsLoading(false);
          }}
          primary
          disabled={isLoading}
          loading={isLoading}
          text={
            paymentType === PAYMENT_TYPE.CARD
              ? "Confirm payment"
              : "Confirm invoice request"
          }
        />
        <Button border onClick={handleClose} text="Cancel" textOnly />
      </ButtonControls>
    </CheckoutModal>
  );
};
